import React, { useState, useEffect } from 'react';
import { useContent } from '../../contexts/ContentContext';
import ContentEditor from './ContentEditor';
import AdminSettings from './AdminSettings';
import SimpleFormationsAdmin from './SimpleFormationsAdmin';
import CalendarSessionsAdmin from './CalendarSessionsAdmin';
import WhatsAppNumbersAdmin from './WhatsAppNumbersAdmin';
import ContactSubmissions from './ContactSubmissions';
import BackupsRestoreAdmin from './BackupsRestoreAdmin';
import InscriptionSubmissions from './InscriptionSubmissions';
import TestimonialSubmissions from './TestimonialSubmissions';
import ServicesOverviewAdmin from './ServicesOverviewAdmin';
import apiClient from '../../services/apiClient';
import './AdminDashboard.css';
import './admin-shared.css';

const AdminDashboard = ({ onLogout }) => {
  const { content, saveContent, isLoading, hasUnsavedChanges, connectionStatus, contentFromSeed, syncError } = useContent();
  
  // Load last selected section from localStorage
  const [selectedSection, setSelectedSection] = useState(() => {
    const saved = localStorage.getItem('adminSelectedSection');
    return saved || 'home.hero';
  });
  
  const [saveMessage, setSaveMessage] = useState('');
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  
  // Load collapsed categories state from localStorage
  const [collapsedCategories, setCollapsedCategories] = useState(() => {
    const saved = localStorage.getItem('adminCollapsedCategories');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse saved categories:', e);
      }
    }
    return {
      'Page d\'accueil': true,
      'Page À propos': true,
      'Page Services': true,
      'Page Formations': true,
      'Page Experts': true,
      'Page Contact': true,
      'Paramètres globaux': true,
      'Paramètres Admin': true
    };
  });

  // Icons for each category
  const categoryIcons = {
    'Page d\'accueil': 'fa-home',
    'Page À propos': 'fa-info-circle',
    'Page Services': 'fa-briefcase',
    'Page Formations': 'fa-graduation-cap',
    'Page Experts': 'fa-user-md',
    'Page Contact': 'fa-envelope',
    'Paramètres globaux': 'fa-globe',
    'Paramètres Admin': 'fa-user-shield'
  };

  // Define the content structure for the sidebar
  const contentStructure = {
    'Page d\'accueil': {
      'home.hero': 'Bannière principale',
      'home.featuredPrograms': 'Programmes phares',
      'home.featuresHighlight': 'Fonctionnalités en vedette',
      'home.expertTestimonials': 'Témoignages de nos Experts',
      'home.participantTestimonials': 'Témoignages de nos Participants',
      'home.upcomingSessions': 'Sessions à venir',
      'home.expertGallery': 'Galerie Photo/Vidéo',
      'home.cta': 'Appel à l\'action'
    },
    'Page À propos': {
      'about.pageHeader': 'En-tête de page',
      'about.coreValues': 'Valeurs fondamentales',
      'about.threeBlocks': '3 Blocs: Histoire, Vision & Mission',
      'about.chronologie': 'Chronologie - Historique détaillé',
      'about.cta': 'Appel à l\'action'
    },
    'Page Services': {
      'services.pageHeader': 'En-tête de page',
      'services.overview': 'Vue d\'ensemble / Statistiques',
      'services.mainServices': 'Services Principaux - 4 Secteurs',
      'services.cta': 'Appel à l\'action'
    },
    'Page Formations': {
      'formations.pageHeader': 'En-tête de page',
      'formations.specialties': 'Spécialités et catalogues',
      'formations.formationsGrid': 'Catalogue des formations (14 cours)',
      'formations.calendar': 'Calendrier Global des Sessions',
      'formations.registrationForm': 'Formulaire d\'inscription',
      'formations.cta': 'Appel à l\'action'
    },
    'Page Experts': {
      'experts.pageHeader': 'En-tête de page',
      'experts.team': 'Équipe de Formateurs Experts',
      'experts.cta': 'Appel à l\'action'
    },
    'Page Témoignages': {
      'testimonials.pageHeader': 'En-tête de page',
      'testimonials.submitSection': 'Formulaire de soumission'
    },
    'Page Contact': {
      'contact.pageHeader': 'En-tête de page',
      'contact.contactInfo': 'Informations de contact',
      'contact.contactForm': 'Formulaire de contact',
      'contact.quickActions': 'Actions rapides',
      'contact.trainingLocations': 'Lieux de formation',
      'contact.registrationLinks': 'Liens d\'inscription rapide',
      'contact.faq': 'Questions fréquentes',
      'contact.interactiveMap': 'Carte interactive',
      'contact.socialNetworks': 'Réseaux sociaux (footer & contact)',
      'contact.cta': 'Appel à l\'action'
    },
    'Paramètres globaux': {
      'global.company': 'Informations de l\'entreprise',
      'global.socialMedia': 'Numéros WhatsApp (formations & CTA)',
    },
    'Paramètres Admin': {
      'admin.settings': 'Informations d\'identification',
      'admin.backups': 'Sauvegardes & Restauration',
      'admin.submissions': 'Demandes de Contact',
      'admin.inscriptions': 'Demandes d\'Inscription',
      'admin.testimonialSubmissions': 'Témoignages Soumis'
    }
  };

  // Fetch current user data
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await apiClient.verifyToken();
        if (response.valid && response.user) {
          setCurrentUser(response.user);
        }
      } catch (error) {
        console.error('Error fetching current user:', error);
      }
    };

    fetchCurrentUser();
  }, []);

  // Automatically expand the category containing the selected section on mount
  useEffect(() => {
    const categoryName = Object.keys(contentStructure).find(category =>
      Object.keys(contentStructure[category]).includes(selectedSection)
    );
    
    if (categoryName && collapsedCategories[categoryName]) {
      setCollapsedCategories(prev => ({
        ...prev,
        [categoryName]: false
      }));
    }
  }, [selectedSection]);

  // Save selected section to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('adminSelectedSection', selectedSection);
  }, [selectedSection]);

  // Save collapsed categories to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('adminCollapsedCategories', JSON.stringify(collapsedCategories));
  }, [collapsedCategories]);

  const handleSave = async () => {
    const result = await saveContent();
    setSaveMessage(result.message);
    setTimeout(() => setSaveMessage(''), 5000);
  };

  const saveDisabled = isLoading || !hasUnsavedChanges || connectionStatus === 'offline' || contentFromSeed;

  const toggleMobileSidebar = () => {
    setIsMobileSidebarOpen(!isMobileSidebarOpen);
  };

  const closeMobileSidebar = () => {
    setIsMobileSidebarOpen(false);
  };

  const toggleCategory = (categoryName) => {
    setCollapsedCategories(prev => ({
      ...prev,
      [categoryName]: !prev[categoryName]
    }));
  };

  const toggleSidebarCollapse = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const renderSidebarItem = (key, label, level = 0) => (
    <div
      key={key}
      className={`sidebar-item level-${level} ${selectedSection === key ? 'active' : ''}`}
      onClick={() => {
        setSelectedSection(key);
        closeMobileSidebar(); // Close mobile sidebar when section is selected
      }}
    >
      <span className="sidebar-label">{label}</span>
      {hasUnsavedChanges && selectedSection === key && (
        <span className="unsaved-indicator">●</span>
      )}
    </div>
  );

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <div className="admin-header">
        <div className="admin-header-left">
          <button 
            className="mobile-sidebar-toggle"
            onClick={toggleMobileSidebar}
            title="Menu de navigation"
          >
            <i className="fas fa-bars"></i>
          </button>
          <h1><i className="fas fa-cog"></i> DentoXcellence CMS</h1>
          <span className="admin-subtitle">Système de Gestion de Contenu</span>
        </div>
        <div className="admin-header-right">
          {hasUnsavedChanges && (
            <span className="unsaved-changes">
              <i className="fas fa-exclamation-circle"></i>
              Modifications non sauvegardées
            </span>
          )}
          <button 
            className={`save-btn ${isLoading ? 'loading' : ''}`}
            onClick={handleSave}
            disabled={saveDisabled}
          >
            {isLoading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Sauvegarde en cours...
              </>
            ) : (
              <>
                <i className="fas fa-save"></i>
                Sauvegarder les modifications
              </>
            )}
          </button>
          <button 
            className="logout-btn"
            onClick={onLogout}
            title="Déconnexion"
          >
            <i className="fas fa-sign-out-alt"></i>
            Déconnexion
          </button>
        </div>
      </div>

      {/* Save Message */}
      {saveMessage && (
        <div className={`save-message ${saveMessage.startsWith('❌') || saveMessage.includes('Erreur') || saveMessage.includes('bloquée') ? 'error' : 'success'}`}>
          <i className={`fas ${saveMessage.startsWith('❌') || saveMessage.includes('Erreur') || saveMessage.includes('bloquée') ? 'fa-exclamation-triangle' : 'fa-check-circle'}`}></i>
          {saveMessage}
        </div>
      )}

      {syncError && (
        <div className="save-message error">
          <i className="fas fa-exclamation-triangle"></i>
          {syncError} — Cliquez sur « Sauvegarder tout » pour réessayer.
        </div>
      )}

      {/* Mobile Overlay */}
      <div 
        className={`mobile-sidebar-overlay ${isMobileSidebarOpen ? 'active' : ''}`}
        onClick={closeMobileSidebar}
      ></div>

      <div className="admin-content">
        {/* Sidebar */}
        <div className={`admin-sidebar ${isMobileSidebarOpen ? 'mobile-open' : ''} ${isSidebarCollapsed ? 'collapsed' : ''}`}>
          <div className="sidebar-header">
            <h3><i className="fas fa-list"></i> Sections de contenu</h3>
            <button 
              className="sidebar-collapse-btn"
              onClick={toggleSidebarCollapse}
              title={isSidebarCollapsed ? 'Développer le menu' : 'Réduire le menu'}
            >
              <i className={`fas fa-chevron-${isSidebarCollapsed ? 'right' : 'left'}`}></i>
            </button>
          </div>
          <div className="sidebar-content">
            {Object.entries(contentStructure).map(([categoryName, categoryItems]) => {
              const isCollapsed = collapsedCategories[categoryName];
              const icon = categoryIcons[categoryName] || 'fa-folder';
              return (
                <div key={categoryName} className="sidebar-category">
                  <div 
                    className="category-header clickable" 
                    onClick={() => toggleCategory(categoryName)}
                  >
                    <h4>
                      <i className={`fas fa-chevron-${isCollapsed ? 'right' : 'down'}`}></i>
                      <i className={`fas ${icon}`}></i>
                      {categoryName}
                    </h4>
                  </div>
                  {!isCollapsed && (
                    <div className="category-items">
                      {Object.entries(categoryItems).map(([key, label]) => 
                        renderSidebarItem(key, label, 1)
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Main Content Editor */}
        <div className="admin-main">
          <div className="editor-header">
            <h2>
              <i className="fas fa-file-medical"></i>
              Modification : {contentStructure[Object.keys(contentStructure).find(category => 
                Object.keys(contentStructure[category]).includes(selectedSection)
              )]?.[selectedSection] || 'Section inconnue'}
            </h2>
          </div>
          
          <div className="editor-content">
            {(connectionStatus === 'offline' || contentFromSeed) && (
              <div
                style={{
                  marginBottom: '1rem',
                  padding: '0.85rem 1rem',
                  borderRadius: '8px',
                  background: '#fff3cd',
                  color: '#664d03',
                  border: '1px solid #ffecb5',
                }}
              >
                <strong>CMS non connecté.</strong> Sauvegarde désactivée pour protéger vos données.
                Rechargez la page — n&apos;enregistrez pas tant que ce message est visible.
              </div>
            )}
            {/* Floating Save Button */}
            {hasUnsavedChanges && connectionStatus === 'online' && !contentFromSeed && (
              <button 
                className={`floating-save-btn ${isLoading ? 'loading' : ''}`}
                onClick={handleSave}
                disabled={isLoading}
                title="Sauvegarder les modifications"
              >
                {isLoading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    <span className="save-text">Sauvegarde...</span>
                  </>
                ) : (
                  <>
                    <i className="fas fa-save"></i>
                    <span className="save-text">Sauvegarder</span>
                  </>
                )}
              </button>
            )}
            
            {selectedSection === 'admin.settings' ? (
              <AdminSettings currentUser={currentUser} />
            ) : selectedSection === 'admin.backups' ? (
              <BackupsRestoreAdmin />
            ) : selectedSection === 'admin.submissions' ? (
              <ContactSubmissions />
            ) : selectedSection === 'admin.inscriptions' ? (
              <InscriptionSubmissions />
            ) : selectedSection === 'admin.testimonialSubmissions' ? (
              <TestimonialSubmissions />
            ) : selectedSection === 'formations.formationsGrid' ? (
              <SimpleFormationsAdmin key={selectedSection} />
            ) : selectedSection === 'formations.calendar' ? (
              <>
                <ContentEditor contentPath={selectedSection} key={`${selectedSection}-editor`} />
                <CalendarSessionsAdmin key={`${selectedSection}-sessions`} />
              </>
            ) : selectedSection === 'services.overview' ? (
              <ServicesOverviewAdmin contentPath={selectedSection} key={selectedSection} />
            ) : selectedSection === 'global.socialMedia' ? (
              <WhatsAppNumbersAdmin key={selectedSection} />
            ) : (
              <ContentEditor 
                contentPath={selectedSection}
                key={selectedSection} // Force re-render when section changes
              />
            )}
          </div>
        </div>
      </div>

    </div>
  );
};

export default AdminDashboard;
